/**
 * API service for DTA Workflow Management. Uses SDK raw methods for typed path/query params + auth.
 */

import { sdk, sdkCall } from "./sdk"

export type {
  WorkflowApproval,
  WorkflowStatus,
  DTAVersionsResponse,
  ListDtaApprovals200Response,
  VersionComparison,
  WorkflowDiagram,
  ActivateDtaWorkflow201Response,
} from "@/generated/sdk"

import type {
  WorkflowApproval,
  WorkflowStatus,
  DTAVersionsResponse,
  ListDtaApprovals200Response,
  VersionComparison,
  WorkflowDiagram,
  ActivateDtaWorkflow201Response,
} from "@/generated/sdk"

const workflowApi = {
  // --- Verb-based workflow actions ---

  async publish(
    dtaId: string,
    reason?: string,
    version?: string,
  ): Promise<ActivateDtaWorkflow201Response> {
    return sdkCall(sdk.publishDta({ dtaId, activateDtaWorkflowRequest: { reason, version } }))
  },

  async approve(
    dtaId: string,
    reason?: string,
    version?: string,
  ): Promise<ActivateDtaWorkflow201Response> {
    return sdkCall(sdk.approveDta({ dtaId, approveDtaRequest: { reason, version } }))
  },

  async activate(
    dtaId: string,
    reason?: string,
    version?: string,
  ): Promise<ActivateDtaWorkflow201Response> {
    return sdkCall(
      sdk.activateDtaWorkflow({ dtaId, activateDtaWorkflowRequest: { reason, version } }),
    )
  },

  // --- Backward transition (request changes) - deprecated, use dedicated endpoint when available ---

  // --- Approvals (read) ---

  async submitApproval(
    dtaId: string,
    stage: string,
    approvalComment?: string,
  ): Promise<WorkflowApproval> {
    return sdkCall(
      sdk.submitApproval({
        dtaId,
        submitApprovalRequest: { stage, approval_comment: approvalComment },
      }),
    )
  },

  async getApprovals(dtaId: string, stage?: string): Promise<ListDtaApprovals200Response> {
    return sdkCall(sdk.listDtaApprovals({ dtaId, stage }))
  },

  async rejectApproval(
    dtaId: string,
    approvalId: string,
    rejectionReason: string,
  ): Promise<WorkflowApproval> {
    return sdkCall(
      sdk.rejectApproval({
        dtaId,
        approvalId,
        rejectApprovalRequest: { status: "REJECTED", rejection_reason: rejectionReason },
      }),
    )
  },

  // --- Workflow status ---

  async getWorkflowStatus(dtaId: string): Promise<WorkflowStatus> {
    return sdkCall(sdk.getDtaWorkflowStatus({ dtaId }))
  },

  async getWorkflowDiagram(dtaId: string): Promise<WorkflowDiagram> {
    return sdkCall(sdk.getWorkflowDiagram({ dtaId }))
  },

  // --- Versions ---

  async getVersions(dtaId: string): Promise<DTAVersionsResponse> {
    return sdkCall(sdk.listDtaVersions({ dtaId }))
  },

  async compareVersions(
    dtaId: string,
    baseVersion: string,
    compareVersion: string,
  ): Promise<VersionComparison> {
    return sdkCall(sdk.compareVersions({ dtaId, baseVersion, compareVersion }))
  },
}

export default workflowApi
